<template>
  <q-toggle
    v-model="model"
    v-bind="$attrs"
    :class="{ 'toggle-card': props.card }"
    unchecked-icon="fas fa-times"
    checked-icon="fas fa-check"
    left-label
  >
    <template #default>
      <div class="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-pl-1">
        <span v-if="props.label" ref="labelRef" class="tw-min-w-0 tw-truncate">
          {{ props.label }}

          <c-tooltip v-if="isLabelTruncated">{{ props.label }}</c-tooltip>
        </span>

        <c-tooltip-standalone
          v-if="props.tooltip"
          class="tw-ml-2 tw-shrink-0"
          size="xs"
        >
          {{ props.tooltip }}
        </c-tooltip-standalone>
      </div>
    </template>

    <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
      <slot :name="slot" v-bind="scope || {}" />
    </template>
  </q-toggle>
</template>

<script setup lang="ts">
import CTooltip from "shared/components/wrappers/tooltip/CTooltip.vue";
import { throttle } from "lodash";
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

const props = withDefaults(
  defineProps<{
    card?: boolean;
    label?: string;
    tooltip?: string;
  }>(),
  {
    card: false,
    label: "",
    tooltip: "",
  },
);

const model = defineModel<boolean>();

const labelRef = useTemplateRef<HTMLSpanElement>("labelRef");
const isLabelTruncated = ref(false);

const checkTruncation = throttle(() => {
  if (labelRef.value === null) {
    return;
  }

  isLabelTruncated.value =
    labelRef.value.scrollWidth > labelRef.value.clientWidth;
}, 100);

let observer: ResizeObserver | null = null;

onMounted(() => {
  if (labelRef.value === null) {
    return;
  }

  checkTruncation();

  observer = new ResizeObserver(checkTruncation);
  observer.observe(labelRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  checkTruncation.cancel();
});
</script>

<style lang="scss">
.q-toggle {
  @apply tw-max-w-full tw-select-none;

  .q-toggle__label {
    @apply tw-min-w-0 tw-flex-1 tw-overflow-hidden;
  }

  .q-toggle__inner--truthy {
    @apply tw-text-green;
  }

  &.reverse {
    @apply tw-justify-between;
  }

  &.toggle-card {
    @apply tw-overflow-hidden tw-rounded-lg tw-bg-slate-100 tw-px-4;
  }

  // Disabled state - allow pointer events for tooltips but block toggle interaction
  &.disabled {
    pointer-events: auto !important;
    opacity: 1 !important;

    .q-toggle__label {
      @apply tw-opacity-75;
    }

    .q-toggle__inner {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>
